import {
  Component,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvTabBase } from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { NewPoiDialogData, POIDto } from '@pages/poi-page/interfaces';
import TripUtils from '@pages/trips-page/shared/trip.utils';
import { Coordinates, MapInput } from '@features/map/interfaces';
import { map, Observable, take, tap } from 'rxjs';
import { TripInformationTabForm } from '@pages/trips-page/components/trip-information-tab/interfaces';
import { NewPoiDialogComponent } from './new-poi-dialog/new-poi-dialog.component';
import { DialogService } from '@core/services/dialog.service';
import { AddressSearchService } from '@features/map/services/address-search.service';
import { AddressLookupResp } from '@shared/interfaces/address-lookup';
import { MapId } from '@features/map/constants/map-id.enum';

@Component({
  selector: 'app-trip-on-map-tab',
  templateUrl: './trip-on-map-tab.component.html',
  styleUrls: ['./trip-on-map-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripOnMapTabComponent extends DvTabBase implements OnInit {
  private api = inject(RestApiService);
  private dialog = inject(DialogService);
  private translate = inject(TranslateService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private addressSearchService = inject(AddressSearchService);

  tripMarkers = signal<MapInput[]>([]);
  isFetchInProgress: boolean;

  hasEditPermissions$: Observable<boolean>;
  isMapVisible$: Observable<boolean>;
  showRouteMarkers: boolean;

  readonly dialogType = {
    StartPOI: 'Trip.Start',
    EndPOI: 'Trip.End',
  };
  readonly mapId = MapId.TripOnMapTabMap;

  private startEndCoordinates: Pick<
    TripInformationTabForm,
    'StartGPSLat' | 'StartGPSLon' | 'EndGPSLat' | 'EndGPSLon'
  >;

  ngOnInit(): void {
    this.hasEditPermissions$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.POIEdit,
    ]);
    this.isMapVisible$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.TripShowInMap,
    ]);
  }

  getAddress(type: string): void {
    this.isFetchInProgress = true;
    const coordinates: Coordinates =
      type === this.dialogType.StartPOI
        ? {
            lat: this.startEndCoordinates.StartGPSLat,
            lng: this.startEndCoordinates.StartGPSLon,
          }
        : {
            lat: this.startEndCoordinates.EndGPSLat,
            lng: this.startEndCoordinates.EndGPSLon,
          };
    this.getAddressToGpsPosition(coordinates)
      .pipe(
        take(1),
        tap(resp => {
          this.isFetchInProgress = false;
          this.openNewPoiDialog(type, {
            GPSLat: coordinates.lat,
            GPSLon: coordinates.lng,
            ...resp,
          });
        })
      )
      .subscribe();
  }

  onEntityUpdate(): void {
    this.api
      .getInfo<TripInformationTabForm>(Entity.Trip, this.entityId, [
        'Distance',
        'TripPoints',
        'StartGPSLat',
        'StartGPSLon',
        'EndGPSLat',
        'EndGPSLon',
        'AllowTripTrackingDetail',
      ])
      .pipe(take(1))
      .subscribe((res: TripInformationTabForm) => {
        this.setTripMarkerInput(res);
        this.showRouteMarkers = !!res.AllowTripTrackingDetail;
        this.startEndCoordinates = {
          StartGPSLat: res.StartGPSLat,
          StartGPSLon: res.StartGPSLon,
          EndGPSLat: res.EndGPSLat,
          EndGPSLon: res.EndGPSLon,
        };
      });
  }

  onEntityCreate(): void {}
  onBaseActions(): void {}

  private setTripMarkerInput(tripForm: TripInformationTabForm): void {
    const tripPointInputs = TripUtils.getTripMapInput(
      tripForm,
      this.showRouteMarkers
    );
    this.tripMarkers.set(tripPointInputs);
  }

  private openNewPoiDialog(
    type: string,
    data: Partial<POIDto> | undefined
  ): void {
    this.dialog.open<NewPoiDialogData, NewPoiDialogComponent>({
      size: 'xl',
      title:
        type === this.dialogType.StartPOI
          ? 'Trip.OnMap.AddStartPOI.Title'
          : 'Trip.OnMap.AddEndPOI.Title',
      confirmText: 'General.Save',
      cancelText: 'General.Cancel',
      hideFooter: true,
      contentComponent: NewPoiDialogComponent,
      data: { ...data, tooltip: this.translate.instant(type) },
    });
  }

  private getAddressToGpsPosition(
    coordinates: Coordinates
  ): Observable<Partial<POIDto> | undefined> {
    return this.addressSearchService
      .getAddressFromGPSPosition(coordinates)
      .pipe(
        map(resp => {
          const address = resp[0] as AddressLookupResp | undefined;
          if (!address) return;

          const valuesArray = Object.values(address) as string[];
          if (!valuesArray.some(v => v)) {
            return undefined;
          } else {
            return address;
          }
        })
      );
  }
}
